import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Navigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  BookOpen, Feather, Flame, Target, Library, Users, ListOrdered,
  Sparkles, Download, FileText, FileType2, FileCode2, Trash2, Plus,
  Eye, EyeOff, Save, ChevronRight, GripVertical, Lock, Check, Edit3,
} from 'lucide-react';
import './book.css';
import {
  ChaptersPanel,
  ResearchPanel,
  CharactersPanel,
  BeatsPanel,
  StatsPanel,
  SettingsPanel,
  AiCowriter,
} from './panels';

const TABS = [
  { key: 'manuscript', label: 'Manuscript', icon: Feather },
  { key: 'research', label: 'Research', icon: Library },
  { key: 'characters', label: 'People', icon: Users },
  { key: 'beats', label: 'Beats', icon: ListOrdered },
  { key: 'stats', label: 'Stats', icon: Target },
  { key: 'settings', label: 'Setup', icon: BookOpen },
];

export default function BookStudioPage() {
  const { user, api } = useAuth();
  const [meta, setMeta] = useState(null);
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState('manuscript');
  const [focusMode, setFocusMode] = useState(false);

  const loadMeta = useCallback(async () => {
    try {
      const { data } = await api.get('/book/meta');
      setMeta(data);
    } catch (e) {
      toast.error('Failed to load book');
    }
  }, [api]);

  const loadStats = useCallback(async () => {
    try {
      const { data } = await api.get('/book/stats');
      setStats(data);
    } catch (e) {
      // soft fail
    }
  }, [api]);

  useEffect(() => {
    if (user?.book_access) {
      loadMeta();
      loadStats();
    }
  }, [user, loadMeta, loadStats]);

  if (!user) return null;
  if (!user.book_access) {
    return <Navigate to="/dashboard" replace />;
  }

  const downloadExport = async (format) => {
    try {
      const resp = await api.get(`/book/export?format=${format}`, { responseType: 'blob' });
      const ext = format === 'markdown' ? 'md' : format;
      const blob = new Blob([resp.data]);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const safe = (meta?.title || 'manuscript').replace(/[^A-Za-z0-9._-]+/g, '_');
      a.download = `${safe}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Exported as .${ext}`);
    } catch (e) {
      toast.error('Export failed');
    }
  };

  return (
    <div className={`book-studio paper ${focusMode ? 'focus-mode' : ''}`} data-testid="book-studio">
      {/* Header */}
      <header className="focus-hide border-b rule px-6 md:px-10 py-6 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="small-caps mb-2 flex items-center gap-2"><Lock className="h-3 w-3" /> Private studio · only you</div>
          <h1 className="book-display text-4xl md:text-5xl font-semibold leading-tight" data-testid="book-title">
            {meta?.title || 'The Young CEO'}
          </h1>
          {meta?.subtitle && (
            <p className="text-lg italic text-[var(--ink-soft)] mt-1">{meta.subtitle}</p>
          )}
        </div>
        <div className="flex items-center gap-3">
          {stats && (
            <div className="text-right ui-font">
              <div className="text-[10px] uppercase tracking-widest text-[var(--ink-fade)]">Today · Streak</div>
              <div className="text-2xl font-semibold text-[var(--ink)] flex items-center gap-2 justify-end" data-testid="streak-display">
                <Flame className="h-5 w-5 text-[var(--rust)]" />
                {stats.streak}d
                <span className="text-[var(--ink-fade)] text-base font-normal">· {stats.today_words}w today</span>
              </div>
            </div>
          )}
          <ExportMenu onPick={downloadExport} />
          <button onClick={() => setFocusMode(!focusMode)} className="btn-ghost" title="Focus mode" data-testid="focus-toggle">
            {focusMode ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
          </button>
        </div>
      </header>

      {/* Tabs */}
      <nav className="focus-hide border-b rule px-4 md:px-10 flex overflow-x-auto" data-testid="book-tabs">
        {TABS.map(t => {
          const Icon = t.icon;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`tab-btn flex items-center gap-2 ${tab === t.key ? 'active' : ''}`}
              data-testid={`tab-${t.key}`}>
              <Icon className="h-3.5 w-3.5" />
              {t.label}
            </button>
          );
        })}
      </nav>

      {/* Body */}
      <main className="px-4 md:px-10 py-8 max-w-7xl">
        {tab === 'manuscript' && (
          <ManuscriptView api={api} onSaveTick={loadStats} focusMode={focusMode} />
        )}
        {tab === 'research' && <ResearchPanel api={api} />}
        {tab === 'characters' && <CharactersPanel api={api} />}
        {tab === 'beats' && <BeatsPanel api={api} />}
        {tab === 'stats' && <StatsPanel stats={stats} onRefresh={loadStats} />}
        {tab === 'settings' && (
          <SettingsPanel api={api} meta={meta} onSaved={loadMeta} />
        )}
      </main>
    </div>
  );
}

function ExportMenu({ onPick }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button onClick={() => setOpen(!open)} className="btn-ink flex items-center gap-1.5" data-testid="export-menu">
        <Download className="h-4 w-4" /> Export
      </button>
      {open && (
        <div className="absolute right-0 mt-1 sheet z-20 w-48" onMouseLeave={() => setOpen(false)}>
          <button onClick={() => { onPick('pdf'); setOpen(false); }} className="w-full text-left px-4 py-2.5 hover:bg-[var(--paper-deep)] flex items-center gap-2 text-sm" data-testid="export-pdf">
            <FileText className="h-4 w-4 text-[var(--rust)]" /> PDF (formatted)
          </button>
          <button onClick={() => { onPick('docx'); setOpen(false); }} className="w-full text-left px-4 py-2.5 hover:bg-[var(--paper-deep)] flex items-center gap-2 text-sm" data-testid="export-docx">
            <FileType2 className="h-4 w-4 text-[#2A4FAA]" /> Word (.docx)
          </button>
          <button onClick={() => { onPick('markdown'); setOpen(false); }} className="w-full text-left px-4 py-2.5 hover:bg-[var(--paper-deep)] flex items-center gap-2 text-sm" data-testid="export-md">
            <FileCode2 className="h-4 w-4 text-[var(--ink-soft)]" /> Markdown
          </button>
        </div>
      )}
    </div>
  );
}

// =====================================================================
// Manuscript view — chapter navigator + editor + AI sidebar
// =====================================================================

function ManuscriptView({ api, onSaveTick, focusMode }) {
  const [chapters, setChapters] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [draft, setDraft] = useState({ title: '', summary: '', content: '', status: 'draft' });
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const saveTimer = useRef(null);
  const lastSavedWords = useRef(0);

  const loadChapters = useCallback(async () => {
    try {
      const { data } = await api.get('/book/chapters');
      setChapters(data);
      if (data.length && !activeId) {
        setActiveId(data[0].id);
      }
    } catch (e) {
      toast.error('Failed to load chapters');
    }
  }, [api, activeId]);

  useEffect(() => { loadChapters(); /* eslint-disable-next-line */ }, []);

  useEffect(() => {
    const active = chapters.find(c => c.id === activeId);
    if (active) {
      setDraft({
        title: active.title || '',
        summary: active.summary || '',
        content: active.content || '',
        status: active.status || 'draft',
      });
      lastSavedWords.current = active.word_count || 0;
      setDirty(false);
    }
  }, [activeId, chapters]);

  const wordCount = countWords(draft.content);

  // Debounced autosave
  const persist = useCallback(async () => {
    if (!activeId || !dirty) return;
    setSaving(true);
    try {
      const { data } = await api.put(`/book/chapters/${activeId}`, draft);
      setChapters(prev => prev.map(c => c.id === activeId ? data : c));
      // Log writing session (delta words)
      const delta = (data.word_count || 0) - lastSavedWords.current;
      if (delta > 0) {
        await api.post('/book/sessions/log', { words_written: delta, minutes: 0 });
        onSaveTick?.();
      }
      lastSavedWords.current = data.word_count || 0;
      setDirty(false);
    } catch (e) {
      toast.error('Save failed');
    }
    setSaving(false);
  }, [activeId, draft, dirty, api, onSaveTick]);

  useEffect(() => {
    if (!dirty) return;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(persist, 1200);
    return () => clearTimeout(saveTimer.current);
  }, [draft, dirty, persist]);

  const updateField = (k, v) => {
    setDraft(prev => ({ ...prev, [k]: v }));
    setDirty(true);
  };

  const addChapter = async () => {
    try {
      const { data } = await api.post('/book/chapters', {
        title: `Chapter ${chapters.length + 1}`,
        content: '',
      });
      setChapters(prev => [...prev, data]);
      setActiveId(data.id);
      toast.success('Chapter added');
    } catch (e) {
      toast.error('Could not create chapter');
    }
  };

  const deleteChapter = async (id) => {
    if (!window.confirm('Delete this chapter? This cannot be undone.')) return;
    try {
      await api.delete(`/book/chapters/${id}`);
      const next = chapters.filter(c => c.id !== id);
      setChapters(next);
      if (activeId === id) setActiveId(next[0]?.id || null);
      toast.success('Chapter deleted');
    } catch (e) {
      toast.error('Delete failed');
    }
  };

  const insertAtCursor = (text) => {
    if (!text) return;
    updateField('content', (draft.content || '') + (draft.content ? '\n\n' : '') + text);
  };

  const replaceContent = (text) => {
    if (!text) return;
    updateField('content', text);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6">
      {/* Chapter navigator */}
      <aside className={`space-y-2 focus-hide ${focusMode ? 'hidden' : ''}`} data-testid="chapter-nav">
        <div className="flex items-center justify-between">
          <h3 className="small-caps">Chapters · {chapters.length}</h3>
          <button onClick={addChapter} className="btn-ghost" data-testid="add-chapter">
            <Plus className="h-3.5 w-3.5" />
          </button>
        </div>
        <div className="space-y-1.5 max-h-[70vh] overflow-y-auto pr-1">
          {chapters.length === 0 && (
            <div className="text-center text-[var(--ink-fade)] py-8 text-sm italic">
              No chapters yet.<br />Click + to begin.
            </div>
          )}
          {chapters.map((c, i) => (
            <button
              key={c.id}
              onClick={() => setActiveId(c.id)}
              className={`chapter-row w-full text-left rounded p-2.5 ${activeId === c.id ? 'active' : ''}`}
              data-testid={`chapter-row-${c.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] uppercase tracking-widest text-[var(--ink-fade)] mb-0.5">{romanize(i + 1)}</div>
                  <div className="text-[15px] font-medium text-[var(--ink)] truncate">{c.title || 'Untitled'}</div>
                  <div className="text-[11px] text-[var(--ink-fade)] mt-0.5">
                    {c.word_count || 0} words · <span className={`chip ${chipClass(c.status)} !text-[8px] !py-0`}>{c.status}</span>
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>
      </aside>

      {/* Editor */}
      <section className="space-y-4">
        {!activeId ? (
          <div className="sheet p-12 text-center">
            <BookOpen className="h-10 w-10 mx-auto text-[var(--ink-fade)] mb-4" />
            <h2 className="book-display text-2xl mb-2">A blank page is the beginning of every book.</h2>
            <p className="text-[var(--ink-soft)] italic mb-6">Create your first chapter to start writing.</p>
            <button onClick={addChapter} className="btn-ink" data-testid="add-first-chapter">
              <Plus className="h-4 w-4 inline mr-1" /> Begin Chapter One
            </button>
          </div>
        ) : (
          <>
            {/* Chapter header */}
            <div className="focus-hide flex items-center gap-3 flex-wrap">
              <input
                value={draft.title}
                onChange={e => updateField('title', e.target.value)}
                className="book-input !text-2xl flex-1 min-w-0 !border-0 !bg-transparent !p-0 focus:!shadow-none focus:!ring-0 focus:!border-0"
                style={{ fontWeight: 600 }}
                placeholder="Chapter title"
                data-testid="chapter-title-input"
              />
              <select
                value={draft.status}
                onChange={e => updateField('status', e.target.value)}
                className="book-input !w-auto ui-font !text-xs"
                data-testid="chapter-status">
                <option value="draft">Draft</option>
                <option value="in_progress">In progress</option>
                <option value="done">Done</option>
              </select>
              <button onClick={() => deleteChapter(activeId)} className="btn-ghost text-[var(--rust)]" data-testid="delete-chapter">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>

            <input
              value={draft.summary}
              onChange={e => updateField('summary', e.target.value)}
              className="book-input focus-hide italic"
              placeholder="One-line summary or hook (italics in the manuscript)"
              data-testid="chapter-summary-input"
            />

            <div className="ornament focus-hide" />

            <textarea
              value={draft.content}
              onChange={e => updateField('content', e.target.value)}
              className="manuscript-editor"
              rows={focusMode ? 30 : 22}
              placeholder="Begin here…"
              data-testid="manuscript-editor"
            />

            <div className="flex items-center justify-between text-xs ui-font text-[var(--ink-fade)] focus-hide">
              <div className="flex items-center gap-4">
                <span data-testid="word-count">{wordCount} words</span>
                <span>·</span>
                <span>{readingTime(wordCount)} read</span>
              </div>
              <div className="flex items-center gap-2">
                {saving ? (
                  <span className="text-[var(--accent)] italic">Saving…</span>
                ) : dirty ? (
                  <span className="text-[var(--ink-fade)] italic">Unsaved</span>
                ) : (
                  <span className="text-[var(--leaf)] flex items-center gap-1"><Check className="h-3 w-3" /> Saved</span>
                )}
                <button onClick={persist} className="btn-ghost" disabled={!dirty || saving} data-testid="save-now">
                  <Save className="h-3.5 w-3.5" />
                </button>
                <button onClick={() => setAiOpen(!aiOpen)} className="btn-accent flex items-center gap-1" data-testid="toggle-ai">
                  <Sparkles className="h-3.5 w-3.5" /> {aiOpen ? 'Hide' : 'Co-writer'}
                </button>
              </div>
            </div>

            {aiOpen && !focusMode && (
              <AiCowriter
                api={api}
                currentText={draft.content}
                onInsert={insertAtCursor}
                onReplace={replaceContent}
              />
            )}
          </>
        )}
      </section>
    </div>
  );
}

// Helpers
function countWords(text) {
  if (!text) return 0;
  return text.replace(/[#*_`>~[\]()]/g, ' ').split(/\s+/).filter(Boolean).length;
}
function readingTime(words) {
  const m = Math.max(1, Math.round(words / 220));
  return `${m} min`;
}
function chipClass(status) {
  if (status === 'done') return 'chip-done';
  if (status === 'in_progress') return 'chip-progress';
  return 'chip-draft';
}
function romanize(n) {
  const map = [['M', 1000], ['CM', 900], ['D', 500], ['CD', 400], ['C', 100], ['XC', 90], ['L', 50], ['XL', 40], ['X', 10], ['IX', 9], ['V', 5], ['IV', 4], ['I', 1]];
  let out = '';
  for (const [r, v] of map) {
    while (n >= v) { out += r; n -= v; }
  }
  return out;
}
